import React from 'react';
import { ThemeProvider } from '@emotion/react';
import type { PageProps } from 'gatsby';
import type { ImageDataLike } from 'gatsby-plugin-image';
import type { BrandName } from 'packages/types/brand-name';
import {
  createBrandTheme,
  getBrandFontUrl,
} from '@agria/theme/src/styles/brandThemes';
import { SitewideMessage } from '@agria/theme/src/components/sitewideMessage/SitewideMessage';
import { SimpleHeader } from '@agria/theme/src/components/headers/simpleHeader/SimpleHeader';
import { SkipLink } from '@agria/theme/src/components/skipLink/SkipLink';
import { useSiteCountry } from '@agria/theme/src/hooks/useSiteCountry';
import { useSiteMetadata } from '@agria/theme/src/hooks/useSiteMetadata';
import Header from '../slices/Header';
import Footer from '../slices/Footer';

export const BaseLayout = ({
  brand,
  location,
  children,
  useMinimalLayout,
  eventLogo,
}: React.PropsWithChildren<{
  brand?: BrandName | null;
  location?: PageProps['location'];
  useMinimalLayout?: boolean;
  eventLogo?: ImageDataLike;
}>) => {
  const { contactNumber, companyName, useSimpleHeader } = useSiteMetadata();
  const { isIe } = useSiteCountry();

  const brandFontUrl = getBrandFontUrl({ brand, isEvent: useMinimalLayout });

  return (
    <ThemeProvider
      theme={createBrandTheme({ brand, isEvent: useMinimalLayout })}
    >
      {brandFontUrl && <style>{`@import url('${brandFontUrl}');`}</style>}

      <SkipLink />

      {useSimpleHeader || useMinimalLayout ? (
        <SimpleHeader
          contactNumber={contactNumber}
          companyName={companyName}
          secondaryLogo={eventLogo}
          isBrochureware
        />
      ) : (
        <Header brand={brand} location={location} />
      )}

      {!isIe && (
        <SitewideMessage
          message="We'll be performing essential maintenance on Tuesday 9th July, from 9pm to midnight. Please come back outside of these times to cover your pet."
          startTime="2024-07-09T21:00:00"
          endTime="2024-07-09T11:59:00"
        />
      )}
      <main id="main">{children}</main>
      <Footer />
    </ThemeProvider>
  );
};
